import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { NewTabLink } from '../../../new-tab-link/index.tsx';
import { PaddedSection } from '../../styled.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { messages };

const getCustomFieldsLink = () =>
	expVal('jira-admin-pages-centralised-fields-m1-experiment', 'isEnabled', false)
		? '/jira/settings/issues/fields'
		: '/secure/admin/ViewCustomFields.jspa';

export const SidebarFindFieldHelpLinkToGlobalSettings = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Show appropriate messages for site + Jira admins. See https://hello.atlassian.net/wiki/spaces/JPCE/pages/1234159989/FE+How+do+I+tell+which+admin+am+I#Tenant-context
	const isGlobalAdmin = useIsAdmin();

	const buttonLink = isGlobalAdmin
		? getCustomFieldsLink()
		: '/secure/ContactAdministrators!default.jspa';

	const buttonLinkDescriptor = isGlobalAdmin ? messages.helpLink : messages.contactAdminLink;
	const buttonClickedEventName = isGlobalAdmin ? 'goToCustomFields' : 'contactYourJiraAdmin';

	const buttonOnClick = (_: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, buttonClickedEventName, {
			isAuxClick: false,
		});
	};

	const buttonOnAuxClick = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', buttonClickedEventName, {
			isAuxClick: true,
		});
	};

	return (
		<PaddedSection>
			<TextWithSpacing>{formatMessage(messages.helpMessage)}</TextWithSpacing>
			<NewTabLink href={buttonLink} onClick={buttonOnClick} onAuxClick={buttonOnAuxClick}>
				{formatMessage(buttonLinkDescriptor)}
			</NewTabLink>
		</PaddedSection>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextWithSpacing = styled.div({
	color: token('color.text'),
	marginBottom: token('space.100'),
});
